import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Link, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  COOKIE_KEY, 
  POLICY_VERSION, 
  CookieConsent, 
  shouldShowCookieBanner 
} from '../utils/cookieConsent';
import { loadAnalytics } from '../utils/analytics';

// 180 days in milliseconds
const CONSENT_TTL = 180 * 24 * 60 * 60 * 1000;

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Use the utility function to determine if banner should be shown
    if (shouldShowCookieBanner()) {
      setOpen(true);
    } else {
      const savedConsent = localStorage.getItem(COOKIE_KEY);
      if (savedConsent) {
        try {
          const parsedConsent: CookieConsent = JSON.parse(savedConsent);
          if (parsedConsent.acceptedAnalytics) {
            loadAnalytics();
          }
        } catch (e) {
          // Invalid JSON, ignore
        }
      }
    }
  }, []);

  const handleAccept = (acceptAnalytics: boolean) => {
    const consentData: CookieConsent = {
      version: POLICY_VERSION,
      timestamp: Date.now(),
      acceptedAnalytics: acceptAnalytics,
    };

    // Store consent in localStorage
    localStorage.setItem(COOKIE_KEY, JSON.stringify(consentData));
    
    // Set cookie for server-side access (optional)
    document.cookie = `cookie_consent=${acceptAnalytics ? 'analytics' : 'essential'}; Path=/; Max-Age=${CONSENT_TTL / 1000}; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;
    
    setOpen(false);
    
    if (acceptAnalytics) {
      loadAnalytics();
    }
  };

  const navigateToPrivacy = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/legal/privacy-policy');
  };

  if (!open) {
    return null;
  }

  return (
    <Paper 
      elevation={3}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        p: 2,
        backgroundColor: 'background.paper',
        zIndex: 9999,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Typography variant="body2" sx={{ flex: '1 1 auto' }}>
        WaveScoop uses cookies to keep you logged in and (optionally) collect anonymous usage analytics via Google Analytics and other services.{' '}
        <Link href="/legal/privacy-policy" onClick={navigateToPrivacy} underline="hover">
          Learn more
        </Link>
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => handleAccept(true)}
          size="small"
        >
          Accept All
        </Button>
        <Button 
          variant="outlined" 
          color="primary" 
          onClick={() => handleAccept(false)}
          size="small"
        >
          Essential Only
        </Button>
      </Box>
    </Paper>
  );
} 